import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import { ExtensionsPaths } from "@dashboard/extensions/urls";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { postConfigurationUpdate } from "../services/configurationApi";

const API_BASE_URL = getAppPlatformUrl("app-configurations");
const APP_NAME = "shipping_estimator";

const ShippingEstimatorView: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [isActive, setIsActive] = useState(false);
  const [initialIsActive, setInitialIsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const tenant = getApiUrl();

  useEffect(() => {
    const loadConfig = async () => {
      if (!tenant) {
        setLoading(false);

        return;
      }

      try {
        const res = await fetch(
          `${API_BASE_URL}/app/get-configuration?tenant=${encodeURIComponent(tenant)}`,
          { headers: { "Content-Type": "application/json" } },
        );

        if (res.ok) {
          const data = await res.json();
          const appConfig = (data as Array<{ app_name: string; is_active: boolean }>).find(
            app => app.app_name === APP_NAME,
          );
          const active = appConfig?.is_active ?? false;

          setIsActive(active);
          setInitialIsActive(active);
        }
      } catch (err) {
        console.error("Failed to load shipping estimator config:", err);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [tenant]);

  const handleSave = async () => {
    if (!tenant) return;

    setSaving(true);

    try {
      const res = await postConfigurationUpdate({
        tenant,
        app_name: APP_NAME,
        configurations: {},
        is_active: isActive,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setInitialIsActive(isActive);
      notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
    } catch {
      notify({ status: "error", text: intl.formatMessage(commonMessages.somethingWentWrong) });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = isActive !== initialIsActive;

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav href={ExtensionsPaths.installedExtensions} title="Shipping Estimator" />
      <DetailPageLayout.Content>
        <Box paddingX={8} paddingY={6} paddingTop={9}>
          <Box
            as="h1"
            style={{ fontSize: "24px", fontWeight: 500, color: "#202124", marginBottom: "8px" }}
          >
            Shipping Estimator
          </Box>
          <Box
            as="div"
            style={{ fontSize: "14px", color: "#5f6368", marginBottom: "24px", lineHeight: "1.5" }}
          >
            Allow customers to estimate shipping costs from the cart page by entering their zip
            code. Rates are calculated using the installed shipping carrier apps (e.g. FedEx, UPS).
          </Box>

          <Box
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            {loading ? (
              <Text>Loading...</Text>
            ) : (
              <Box style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <label
                  style={{
                    position: "relative",
                    display: "inline-block",
                    width: "44px",
                    height: "24px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0, position: "absolute" }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: isActive ? "#2563eb" : "#d1d5db",
                      borderRadius: "24px",
                      transition: "background-color 0.2s",
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      top: "3px",
                      left: isActive ? "23px" : "3px",
                      width: "18px",
                      height: "18px",
                      backgroundColor: "white",
                      borderRadius: "50%",
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  />
                </label>
                <Box display="flex" flexDirection="column">
                  <Text fontWeight="medium">{isActive ? "Enabled" : "Disabled"}</Text>
                  <Text size={3} color="default2" style={{ marginTop: "2px" }}>
                    {isActive
                      ? "The zip code estimator widget is visible in the cart summary."
                      : "The zip code estimator widget is hidden from the cart."}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DetailPageLayout.Content>

      <Savebar>
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => setIsActive(initialIsActive)} />
        <Savebar.ConfirmButton
          transitionState={saving ? "loading" : "default"}
          onClick={handleSave}
          disabled={saving || loading || !hasChanges}
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default ShippingEstimatorView;
